/**
 * Carousel Component
 *
 * Simple carousel/slider for testimonials and similar content.
 *
 * @package TheRealEstates
 */

import { gsap } from 'gsap';

export default class Carousel {
  constructor(element, options = {}) {
    this.container = element;

    if (!this.container) return;

    this.options = {
      autoplay: true,
      autoplayDelay: 5000,
      pauseOnHover: true,
      ...options,
    };

    this.track = this.container.querySelector('[data-carousel-track]');
    this.slides = [...this.container.querySelectorAll('[data-carousel-slide]')];
    this.prevBtn = this.container.querySelector('[data-carousel-prev]');
    this.nextBtn = this.container.querySelector('[data-carousel-next]');
    this.dotsContainer = this.container.querySelector('[data-carousel-dots]');

    this.currentIndex = 0;
    this.isAnimating = false;
    this.autoplayInterval = null;
    this.dots = [];

    if (this.slides.length > 0) {
      this.init();
    }
  }

  /**
   * Initialize carousel
   */
  init() {
    this.createDots();
    this.setInitialState();
    this.bindEvents();

    if (this.options.autoplay) {
      this.startAutoplay();
    }
  }

  /**
   * Create dot indicators
   */
  createDots() {
    if (!this.dotsContainer) return;

    this.slides.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.className = 'carousel-dot';
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.setAttribute('type', 'button');
      dot.addEventListener('click', () => this.goTo(index));
      this.dotsContainer.appendChild(dot);
      this.dots.push(dot);
    });
  }

  /**
   * Set initial state
   */
  setInitialState() {
    // Hide all slides except first
    this.slides.forEach((slide, index) => {
      if (index !== 0) {
        gsap.set(slide, { opacity: 0, display: 'none' });
      }
      slide.setAttribute('aria-hidden', index !== 0);
    });

    this.updateDots();
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Navigation buttons
    this.prevBtn?.addEventListener('click', () => this.prev());
    this.nextBtn?.addEventListener('click', () => this.next());

    // Pause on hover
    if (this.options.pauseOnHover) {
      this.container.addEventListener('mouseenter', () => this.stopAutoplay());
      this.container.addEventListener('mouseleave', () => {
        if (this.options.autoplay) {
          this.startAutoplay();
        }
      });
    }

    // Touch/swipe support
    let startX = 0;
    let startY = 0;

    this.container.addEventListener(
      'touchstart',
      (e) => {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      },
      { passive: true }
    );

    this.container.addEventListener(
      'touchend',
      (e) => {
        const endX = e.changedTouches[0].clientX;
        const endY = e.changedTouches[0].clientY;
        const diffX = startX - endX;
        const diffY = startY - endY;

        // Only trigger if horizontal swipe is greater than vertical
        if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 50) {
          diffX > 0 ? this.next() : this.prev();
        }
      },
      { passive: true }
    );

    // Keyboard navigation
    this.container.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prev();
      } else if (e.key === 'ArrowRight') {
        this.next();
      }
    });
  }

  /**
   * Go to specific slide
   */
  goTo(index) {
    if (this.isAnimating || index === this.currentIndex) return;
    if (index < 0 || index >= this.slides.length) return;

    this.isAnimating = true;
    const direction = index > this.currentIndex ? 1 : -1;
    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[index];

    // Timeline for transition
    const tl = gsap.timeline({
      onComplete: () => {
        this.isAnimating = false;
        this.currentIndex = index;
        this.updateDots();
      },
    });

    // Animate out current slide
    tl.to(currentSlide, {
      opacity: 0,
      x: -30 * direction,
      duration: 0.4,
      ease: 'power2.in',
      onComplete: () => {
        gsap.set(currentSlide, { display: 'none' });
        currentSlide.setAttribute('aria-hidden', 'true');
      },
    });

    // Animate in next slide
    tl.set(nextSlide, { display: 'block', x: 30 * direction });
    tl.to(
      nextSlide,
      {
        opacity: 1,
        x: 0,
        duration: 0.5,
        ease: 'power2.out',
        onStart: () => {
          nextSlide.setAttribute('aria-hidden', 'false');
        },
      },
      '-=0.1'
    );

    return tl;
  }

  /**
   * Go to next slide
   */
  next() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goTo(nextIndex);
  }

  /**
   * Go to previous slide
   */
  prev() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goTo(prevIndex);
  }

  /**
   * Update dot indicators
   */
  updateDots() {
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === this.currentIndex);
      dot.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
    });
  }

  /**
   * Start autoplay
   */
  startAutoplay() {
    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.next();
    }, this.options.autoplayDelay);
  }

  /**
   * Stop autoplay
   */
  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  /**
   * Destroy carousel
   */
  destroy() {
    this.stopAutoplay();
    gsap.killTweensOf(this.slides);
  }
}
